import * as types from 'constants/ActionTypes';
import { cdsLoyaltyProgram } from 'js-api-client';
import errorMonitoring from '../errorMonitoring';

/**
 * @param {boolean} isLoading
 * @returns {object}
 */
export function setCdsFpLoading(isLoading) {
  return {
    type: types.SET_CDS_FP_LOADING,
    isLoading,
  };
}

/**
 * @param {string|null} error
 * @returns {object}
 */
export function setCdsFpError(error) {
  return {
    type: types.SET_CDS_FP_ERROR,
    error,
  };
}

/**
 * @returns {object}
 */
export function clearCdsFpError() {
  return {
    type: types.CLEAR_CDS_FP_ERROR,
  };
}

/**
 * @param {object} error
 * @returns {string}
 */
function resolveCdsFpLoginErrorMessage(error) {
  if (error?.status === 401) {
    return 'invalid_credentials';
  }

  const apiMessage = error?.message?.trim();

  return apiMessage || 'login_error';
}

/**
 * @param {string|null} step
 * @returns {object}
 */
export function setCdsFpEnrollmentStep(step) {
  return {
    type: types.SET_CDS_FP_ENROLLMENT_STEP,
    step,
  };
}

/**
 * @param {{ identity_type: string, identity_number: string, email?: string }|null} enrollmentIdentity
 * @returns {object}
 */
export function setCdsFpEnrollmentIdentity(enrollmentIdentity) {
  return {
    type: types.SET_CDS_FP_ENROLLMENT_IDENTITY,
    enrollmentIdentity,
  };
}

/**
 * @param {object|null} activationResult
 * @returns {object}
 */
export function setCdsFpActivationResult(activationResult) {
  return {
    type: types.SET_CDS_FP_ACTIVATION_RESULT,
    activationResult,
  };
}

/**
 * Clears in-progress enrollment / activation state (e.g. after success → login).
 * @returns {Function}
 */
export function resetCdsFpEnrollmentFlow() {
  return (dispatch) => {
    dispatch(setCdsFpEnrollmentStep(null));
    dispatch(setCdsFpEnrollmentIdentity(null));
    dispatch(setCdsFpActivationResult(null));
    dispatch(clearCdsFpError());
  };
}

/**
 * @param {object} [options]
 * @param {boolean} [options.usesReserhubAccount] - Whether Unified Account owns this resolved session.
 * @returns {object}
 */
export function clearCdsFpSession({ usesReserhubAccount = false } = {}) {
  return {
    type: types.CLEAR_CDS_FP_SESSION,
    usesReserhubAccount,
  };
}

/**
 * @param {boolean} [options.silentUnauthorized] - Do not surface 401 on bootstrap.
 * @returns {Function}
 */
export function fetchCdsFrequentPassengerAccountStatus({ silentUnauthorized = false } = {}) {
  return async (dispatch) => {
    dispatch(setCdsFpLoading(true));
    dispatch(clearCdsFpError());

    try {
      const response = await cdsLoyaltyProgram.getAccountStatus();

      dispatch({
        type: types.SET_CDS_FP_ACCOUNT_STATUS,
        accountStatus: response.data ?? null,
        isAuthenticated: true,
      });

      return response;
    } catch (error) {
      if (silentUnauthorized && error?.status === 401) {
        dispatch(clearCdsFpSession());
        return null;
      }

      errorMonitoring.notify({
        error,
        info: { message: 'Error fetching CDS frequent passenger account status' },
      });
      dispatch(setCdsFpError(error?.message || 'generic_error'));
      dispatch(setCdsFpLoading(false));
      throw error;
    }
  };
}

/**
 * @param {object} payload - document_type, document_number, password.
 * @returns {Function}
 */
export function cdsFrequentPassengerLogin(payload) {
  return async (dispatch) => {
    dispatch(setCdsFpLoading(true));
    dispatch(clearCdsFpError());

    try {
      const response = await cdsLoyaltyProgram.login(payload);

      dispatch({
        type: types.SET_CDS_FP_PROGRAM_TOKEN,
        programToken: response.token ?? null,
      });

      return await dispatch(fetchCdsFrequentPassengerAccountStatus());
    } catch (error) {
      dispatch(setCdsFpError(resolveCdsFpLoginErrorMessage(error)));
      dispatch(setCdsFpLoading(false));
      throw error;
    }
  };
}

/**
 * @returns {Function}
 */
export function cdsFrequentPassengerLogout() {
  return async (dispatch) => {
    dispatch(setCdsFpLoading(true));
    dispatch(clearCdsFpError());

    try {
      await cdsLoyaltyProgram.logout();
      dispatch(clearCdsFpSession());
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: { message: 'Error logging out CDS frequent passenger' },
      });
      dispatch(setCdsFpError('logout_error'));
      dispatch(setCdsFpLoading(false));
      throw error;
    }
  };
}

/**
 * @param {object} payload - Enrollment payload.
 * @returns {Function}
 */
export function cdsFrequentPassengerEnrollment(payload) {
  return async (dispatch) => {
    dispatch(setCdsFpLoading(true));
    dispatch(clearCdsFpError());

    try {
      const response = await cdsLoyaltyProgram.enrollment(payload);
      dispatch(
        setCdsFpEnrollmentIdentity({
          identity_type: payload.identity_type,
          identity_number: payload.identity_number,
          email: payload.email,
        }),
      );
      dispatch(setCdsFpEnrollmentStep('email_sent'));
      dispatch(setCdsFpLoading(false));
      return response;
    } catch (error) {
      dispatch(setCdsFpError(error?.message || 'enrollment_error'));
      dispatch(setCdsFpLoading(false));
      throw error;
    }
  };
}

/**
 * @param {object} payload - identity_type, identity_number, activation_code.
 * @returns {Function}
 */
export function cdsFrequentPassengerActivation(payload) {
  return async (dispatch) => {
    dispatch(setCdsFpLoading(true));
    dispatch(clearCdsFpError());

    try {
      const response = await cdsLoyaltyProgram.activation(payload);
      dispatch(
        setCdsFpActivationResult({
          full_name: response.full_name ?? null,
          password: response.password ?? null,
          message: response.message ?? null,
        }),
      );
      dispatch(setCdsFpEnrollmentStep('activated'));
      dispatch(setCdsFpLoading(false));
      return response;
    } catch (error) {
      dispatch(setCdsFpError(error?.message || 'activation_error'));
      dispatch(setCdsFpLoading(false));
      throw error;
    }
  };
}
